import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { useOrganizationStore } from "../../../../store/organization";
import { useEventStore } from "../../../../store/event";
import { userStore } from "../../../../store/user";
import { Attendee, DemographicAnswer } from "../../../../types/attendee";
import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import { PrimaryButton, SecondaryButton } from "../../../../components/Button";
import Loader from "../../../../components/Loader";
import { ATTENDEE_DEMOGRAPHICS } from "./queries";

const client = createApolloClient();

const DEFAULT_FROM = moment().subtract(90, "days").startOf("day").format("YYYY-MM-DD");
const DEFAULT_TO = moment().add(1, "day").startOf("day").format("YYYY-MM-DD");
const TOP_N = 9;

interface BreakdownRow {
  label: string;
  count: number;
}

interface Section {
  key: string;
  title: string;
  totalLabel?: string;
  rows: BreakdownRow[];
  denominator: number;
  sortMode?: "count" | "ageRange";
}

const ageRangeStart = (label: string): number => {
  const match = label.match(/\d+/);
  if (!match) return Number.POSITIVE_INFINITY;
  const num = parseInt(match[0], 10);
  if (/under/i.test(label)) return num - 1;
  return num;
};

const normalizeAnswer = (a: DemographicAnswer): string[] => {
  if (a == null) return [];
  if (Array.isArray(a.answer)) return a.answer.map((s) => String(s).trim()).filter(Boolean);
  if (typeof a.answer === "string" && a.answer.trim()) return [a.answer.trim()];
  return [];
};

const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="flex flex-col px-4 py-3 min-w-0">
    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-xl font-bold text-gray-900 tabular-nums mt-1 truncate">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-0.5 truncate">{sub}</div>}
  </div>
);

const BreakdownSection = ({ section }: { section: Section }) => {
  const sorted =
    section.sortMode === "ageRange"
      ? [...section.rows].sort((a, b) => ageRangeStart(a.label) - ageRangeStart(b.label))
      : [...section.rows].sort((a, b) => b.count - a.count);
  const shown = section.sortMode === "ageRange" ? sorted : sorted.slice(0, TOP_N);
  const others = section.sortMode === "ageRange" ? [] : sorted.slice(TOP_N);
  const otherCount = others.reduce((sum, r) => sum + r.count, 0);
  const denom = section.denominator || 0;

  return (
    <div className="border-t border-gray-200 first:border-t-0">
      <div className="px-4 py-2 bg-gray-50 flex justify-between items-center">
        <div className="text-sm font-semibold text-gray-800 truncate" title={section.title}>
          {section.title}
        </div>
        {section.totalLabel && (
          <div className="text-[11px] uppercase tracking-wide text-gray-500">{section.totalLabel}</div>
        )}
      </div>
      <table className="min-w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {shown.length === 0 && (
            <tr>
              <td colSpan={3} className="px-4 py-3 text-center text-gray-400 text-xs">
                No responses.
              </td>
            </tr>
          )}
          {shown.map((r) => (
            <tr key={r.label}>
              <td className="px-4 py-2 text-gray-800">{r.label}</td>
              <td className="px-4 py-2 text-right tabular-nums w-20">{r.count.toLocaleString()}</td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-500 w-16">
                {denom ? `${((r.count / denom) * 100).toFixed(1)}%` : "—"}
              </td>
            </tr>
          ))}
          {others.length > 0 && (
            <tr className="bg-gray-50/60">
              <td className="px-4 py-2 text-gray-600 italic">All others ({others.length})</td>
              <td className="px-4 py-2 text-right tabular-nums w-20">{otherCount.toLocaleString()}</td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-500 w-16">
                {denom ? `${((otherCount / denom) * 100).toFixed(1)}%` : "—"}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const AttendeeDemographicsReport = () => {
  const [dateFrom, setDateFrom] = useState<string>(DEFAULT_FROM);
  const [dateTo, setDateTo] = useState<string>(DEFAULT_TO);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const headerEventId = useEventStore((s) => s.eventId);

  const buildWhere = useCallback(() => {
    const organizationId = parseInt(headerOrgId !== null ? headerOrgId.toString() : "", 10);
    const eventId = headerEventId;
    const user = userStore.getState().user;

    const where: any = {
      created_at: {
        _gte: moment(dateFrom).startOf("day").toISOString(),
        _lt: moment(dateTo).startOf("day").toISOString(),
      },
    };
    if (organizationId && organizationId !== 0) {
      where.event = { organization_id: { _eq: organizationId } };
    }
    if (eventId) {
      where.event_id = { _eq: eventId };
    } else if (user?.events) {
      where.event_id = { _in: user.events };
    }
    return where;
  }, [dateFrom, dateTo, headerOrgId, headerEventId]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await client.query({
        query: ATTENDEE_DEMOGRAPHICS,
        variables: { where: buildWhere() },
        fetchPolicy: "network-only",
      });
      if (!mountedRef.current) return;
      setAttendees((data?.attendees ?? []) as Attendee[]);
      setHasGenerated(true);
    } catch (err: any) {
      console.error("Demographics report fetch failed:", err);
      if (mountedRef.current) setError(err?.message ?? "Failed to load report");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [buildWhere]);

  const totals = useMemo(() => {
    const totalAttendees = attendees.length;
    const withDemo = attendees.filter((a) => (a.optional_datas?.length ?? 0) > 0).length;
    const active = attendees.filter((a) => a.is_active && !a.is_suspended).length;
    const suspended = attendees.filter((a) => a.is_suspended).length;
    const inactive = totalAttendees - active - suspended;
    const responseRate = totalAttendees ? (withDemo / totalAttendees) * 100 : 0;
    return { totalAttendees, withDemo, active, suspended, inactive, responseRate };
  }, [attendees]);

  const sections = useMemo<Section[]>(() => {
    const out: Section[] = [];

    out.push({
      key: "__status",
      title: "Status",
      totalLabel: `${totals.totalAttendees.toLocaleString()} attendees`,
      denominator: totals.totalAttendees,
      rows: [
        { label: "Active", count: totals.active },
        { label: "Suspended", count: totals.suspended },
        { label: "Inactive / Other", count: totals.inactive },
      ],
    });

    const eventMap = new Map<string, number>();
    attendees.forEach((a) => {
      const name = a.event?.name ?? "Unassigned";
      eventMap.set(name, (eventMap.get(name) ?? 0) + 1);
    });
    if (eventMap.size > 0) {
      out.push({
        key: "__event",
        title: "Event",
        totalLabel: `${totals.totalAttendees.toLocaleString()} attendees`,
        denominator: totals.totalAttendees,
        rows: [...eventMap.entries()].map(([label, count]) => ({ label, count })),
      });
    }

    const byQuestion = new Map<string, { totalResponses: number; answers: Map<string, number> }>();
    for (const a of attendees) {
      const answers = a.optional_datas ?? [];
      for (const ans of answers) {
        if (!ans?.question) continue;
        const key = ans.question;
        let bucket = byQuestion.get(key);
        if (!bucket) {
          bucket = { totalResponses: 0, answers: new Map() };
          byQuestion.set(key, bucket);
        }
        const normalized = normalizeAnswer(ans);
        if (normalized.length === 0) continue;
        bucket.totalResponses += 1;
        for (const value of normalized) {
          bucket.answers.set(value, (bucket.answers.get(value) ?? 0) + 1);
        }
      }
    }
    const questionEntries = [...byQuestion.entries()].sort(
      (a, b) => b[1].totalResponses - a[1].totalResponses
    );
    for (const [question, bucket] of questionEntries) {
      const isZip = /zip/i.test(question);
      const isAge = /\bage\b/i.test(question);
      out.push({
        key: question,
        title: isZip ? "Top Zip Codes" : question,
        totalLabel: `${bucket.totalResponses.toLocaleString()} responses`,
        denominator: bucket.totalResponses,
        rows: [...bucket.answers.entries()].map(([label, count]) => ({ label, count })),
        sortMode: isAge ? "ageRange" : "count",
      });
    }

    return out;
  }, [attendees, totals]);

  const signupTrend = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of attendees) {
      if (!a.created_at) continue;
      const day = moment(a.created_at).format("YYYY-MM-DD");
      counts.set(day, (counts.get(day) ?? 0) + 1);
    }
    const start = moment(dateFrom).startOf("day");
    const end = moment(dateTo).startOf("day");
    const series: { day: string; count: number }[] = [];
    for (let d = start.clone(); d.isBefore(end); d.add(1, "day")) {
      const key = d.format("YYYY-MM-DD");
      series.push({ day: key, count: counts.get(key) ?? 0 });
    }
    return series;
  }, [attendees, dateFrom, dateTo]);

  const maxTrendCount = useMemo(
    () => signupTrend.reduce((acc, p) => Math.max(acc, p.count), 0),
    [signupTrend]
  );

  const yTicks = useMemo(() => {
    if (maxTrendCount <= 0) return [0];
    const niceMax = Math.max(1, Math.ceil(maxTrendCount));
    return [niceMax, Math.round(niceMax / 2), 0];
  }, [maxTrendCount]);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Breadcrumbs />
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <SecondaryButton
              onClick={() => {
                setDateFrom(DEFAULT_FROM);
                setDateTo(DEFAULT_TO);
              }}
            >
              Reset
            </SecondaryButton>
            <PrimaryButton onClick={fetchData} isDisabled={isLoading}>
              {isLoading ? "Loading…" : "Generate"}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {isLoading && <div className="my-6"><Loader /></div>}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 my-4 text-sm">
          {error}
        </div>
      )}

      {!isLoading && !hasGenerated && !error && (
        <div className="bg-white rounded-lg border border-gray-200 px-6 py-12 text-center text-gray-500">
          Pick a date range and click <span className="font-semibold">Generate</span> to view attendee demographics.
        </div>
      )}

      {!isLoading && hasGenerated && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-gray-200 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            <Stat label="Total Attendees" value={totals.totalAttendees.toLocaleString()} />
            <Stat
              label="With Demo Data"
              value={totals.withDemo.toLocaleString()}
              sub={`${totals.responseRate.toFixed(1)}% response rate`}
            />
            <Stat label="Active" value={totals.active.toLocaleString()} />
            <Stat label="Suspended" value={totals.suspended.toLocaleString()} />
            <Stat
              label="Distinct Events"
              value={(sections.find((s) => s.key === "__event")?.rows.length ?? 0).toLocaleString()}
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-2.5 border-b border-gray-200 text-sm font-semibold text-gray-800">
              Profiles Created Over Time
            </div>
            <div className="px-4 py-4">
              {signupTrend.length === 0 || maxTrendCount === 0 ? (
                <div className="text-sm text-gray-400 text-center py-6">No signups in this range.</div>
              ) : (
                <div className="flex gap-2">
                  <div className="flex flex-col justify-between text-[10px] text-gray-500 tabular-nums h-40 py-0.5">
                    {yTicks.map((t) => (
                      <div key={t} className="leading-none">{t}</div>
                    ))}
                  </div>
                  <div className="flex-1 relative border-l border-b border-gray-200 h-40">
                    <div className="absolute inset-0 flex items-stretch gap-px">
                      {signupTrend.map((p) => (
                        <div key={p.day} className="flex-1 flex flex-col justify-end group relative">
                          <div
                            className="w-full bg-red-300 group-hover:bg-red-500 transition-colors rounded-t"
                            style={{
                              height: `${(p.count / maxTrendCount) * 100}%`,
                              minHeight: p.count > 0 ? "2px" : "0",
                            }}
                            title={`${p.day}: ${p.count}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {signupTrend.length > 0 && (
                <div className="flex justify-between text-[10px] text-gray-500 mt-2 pl-8">
                  <span>{signupTrend[0].day}</span>
                  <span>{signupTrend[signupTrend.length - 1].day}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-200 text-sm font-semibold text-gray-800">
              Breakdowns
            </div>
            {sections.map((s) => (
              <BreakdownSection key={s.key} section={s} />
            ))}
            {sections.length === 1 && (
              <div className="px-4 py-6 text-center text-gray-400 text-sm border-t border-gray-100">
                No demographic questions have been answered by attendees in this range.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
